import { action, computed, makeObservable, observable } from 'mobx'

import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  OperatorType,
} from '~/client/graph/types.generated'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import { isEqualDeep } from '~/client/src/shared/utils/util'

export default class DynamicFiltersStore {
  @observable public isCreationMode: boolean = false
  @observable public editingFilterIdx: number = null
  @observable public expandedFilterIdx: number = null
  @observable public isListExpanded: boolean = true

  @observable public filters: ITypeFieldFilterOptions[] = []
  @observable public filterFieldsMap: Map<string, IPermitTypeField> = null
  @observable private onChange: (filters: ITypeFieldFilterOptions[]) => void =
    null
  @observable public formData: ITypeFieldFilterOptions = {
    fieldId: '',
    fieldValues: [],
    operator: OperatorType.In,
    values: [],
  }

  public constructor(
    filters: ITypeFieldFilterOptions[],
    filterFieldsMap: Map<string, IPermitTypeField>,
    onChange: (filters: ITypeFieldFilterOptions[]) => void,
  ) {
    this.setFilters(filters)
    this.setFilterFieldsMap(filterFieldsMap)
    this.setOnChangeHandler(onChange)

    makeObservable(this)
  }

  @action.bound
  public setFilters(filters: ITypeFieldFilterOptions[]) {
    this.filters = [...filters]
    this.editingFilterIdx = null
    this.expandedFilterIdx = null
  }

  @action.bound
  public setFilterFieldsMap(filterFieldsMap: Map<string, IPermitTypeField>) {
    this.filterFieldsMap = filterFieldsMap
  }

  @action.bound
  public setOnChangeHandler(
    onChange: (filters: ITypeFieldFilterOptions[]) => void,
  ) {
    this.onChange = onChange
  }

  @action.bound
  public enableCreationMode() {
    this.isCreationMode = true
    this.editingFilterIdx = null
    this.expandedFilterIdx = null
    this.isListExpanded = true
    this.resetFormData()
  }

  @action.bound
  public toggleFiltersList() {
    this.isListExpanded = !this.isListExpanded
  }

  @action.bound
  public resetData() {
    this.isCreationMode = false
    this.editingFilterIdx = null
    this.resetFormData()
  }

  @action.bound
  public expandFilter(filterIdx: number) {
    this.expandedFilterIdx =
      this.expandedFilterIdx === filterIdx ? null : filterIdx
  }

  @action.bound
  public editFilter(filterIdx: number) {
    const filter = this.filters[filterIdx]

    if (!filter) {
      return
    }

    this.isCreationMode = false
    this.editingFilterIdx = filterIdx
    this.expandedFilterIdx = filterIdx
    this.formData = {
      fieldId: filter.fieldId,
      fieldValues: [...filter.fieldValues],
      operator: filter.operator,
      values: [...filter.values],
    }
  }

  @action.bound
  public updateFieldId(fieldId: string) {
    this.formData.fieldId = fieldId

    this.formData.fieldValues = []
    this.formData.values = []
  }

  @action.bound
  public updateFieldValues(selectedFieldValues: string[]) {
    this.formData.fieldValues = selectedFieldValues
  }

  @action.bound
  public updateValues(selectedValues: string[]) {
    this.formData.values = selectedValues
  }

  @action.bound
  public removeValueFromFilter(locationIds: Set<string>) {
    if (
      this.isCreationMode ||
      (this.editingFilterIdx !== null && this.editingFilterIdx > -1)
    ) {
      this.formData.values = this.formData.values.filter(
        id => !locationIds.has(id),
      )
    }
  }

  @action.bound
  public createNewFilter() {
    if (!this.isFormValid) {
      return
    }

    const newFilter = this.getFilterForSaving()

    if (this.isCreationMode && this.editingFilterIdx === null) {
      this.filters.push(newFilter)
      this.onChange(this.filters)
      this.resetData()
    }
  }

  @action.bound
  public saveFilter() {
    if (!this.isFormValid || this.isCreationMode) {
      return
    }

    const newFilter = this.getFilterForSaving()

    const isFilterChanged = !isEqualDeep(
      this.filters[this.editingFilterIdx],
      newFilter,
    )

    if (isFilterChanged) {
      this.filters[this.editingFilterIdx] = newFilter
      this.onChange(this.filters)
    }

    this.resetData()
  }

  @action.bound
  public removeFilter(filterIdx: number) {
    this.filters = this.filters.filter((_, idx) => idx !== filterIdx)
    this.onChange(this.filters)
  }

  @action.bound
  public cancelEditing() {
    this.editingFilterIdx = null
    this.resetFormData()
  }

  @action.bound
  public cancelOrRemoveFilter = (filterIdx: number) => {
    if (this.editingFilterIdx === filterIdx) {
      this.cancelEditing()
    } else {
      this.removeFilter(filterIdx)
    }
  }

  public editOrSaveFilter = (filterIdx: number) => {
    if (this.editingFilterIdx === filterIdx) {
      this.saveFilter()
    } else {
      this.editFilter(filterIdx)
    }
  }

  @action.bound
  private resetFormData() {
    this.formData = {
      fieldId: '',
      fieldValues: [],
      operator: OperatorType.In,
      values: [],
    }
  }

  @computed
  public get selectedField(): IPermitTypeField {
    return this.filterFieldsMap.get(this.formData.fieldId)
  }

  @computed
  public get isFormValid(): boolean {
    return !!(
      this.formData.fieldId &&
      this.selectedField &&
      this.formData.fieldValues.length > 0 &&
      this.formData.values.length > 0
    )
  }

  private getFilterForSaving(): ITypeFieldFilterOptions {
    return {
      fieldId: this.formData.fieldId,
      fieldValues: this.formData.fieldValues,
      values: this.formData.values,
      operator: OperatorType.In,
      propertyPath: locationFilterPropMap.ID,
    }
  }
}
